import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { useTicketPriorities } from "../../../hooks/TICKET/useTicketPriorities";
import { useTicketTypes } from "../../../hooks/TICKET/useTicketTypes";
import { useTicketStatuts } from "../../../hooks/TICKET/useTicketStatuts";
import { useUsers } from "../../../hooks/useGetUsers";
import { useGroupes } from "../../../hooks/useGetGroupes";
import { useClients } from "../../../hooks/useGetClients";
import { usePostTicket } from "../../../hooks/TICKET/usePostTicket";
import { useFacturationModes } from "../../../hooks/TICKET/useFacturationModes";
import { useContratsByClientId } from "../../../hooks/useGetContratClient";
import { useClientChamps } from "../../../hooks/useClientChamps";
import { getApplicableChamps, TICKET_TO_FIELD_STATUS } from "../../../utils/customFields";
import { postChampValeur } from "../../../api/CHAMPS/champs";
import CustomFieldsLayout from "../../CustomFields/CustomFieldsLayout";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import { customSelectStyles } from "../../../utils/reactSelectStyles";

// ─── Helpers date locale ──────────────────────────────────────────────────────
const _pad = (n) => String(n).padStart(2, "0");

const getLocalDateFormatted = () => {
  const now = new Date();
  return `${now.getFullYear()}-${_pad(now.getMonth() + 1)}-${_pad(now.getDate())}`;
};

const getLocalHourFormatted = () => {
  const now = new Date();
  return `${_pad(now.getHours())}:${_pad(now.getMinutes())}`;
};

const getLocalDateTimeFormatted = () => {
  const now = new Date();
  return `${now.getFullYear()}-${_pad(now.getMonth() + 1)}-${_pad(now.getDate())}T${_pad(now.getHours())}:${_pad(now.getMinutes())}`;
};
// ─────────────────────────────────────────────────────────────────────────────

// Constant for "Maintenance Contract" mode
const CONTRAT_MAINTENANCE = { id: 1, label: "Contrat de maintenance" };

const NewTicket = ({ showNewTicket, setShowNewTicket, defaultClient = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateDebut, setDateDebut] = useState(getLocalDateFormatted());
  const [heureDebut, setHeureDebut] = useState(getLocalHourFormatted());
  const [dateFin, setDateFin] = useState(getLocalDateFormatted());
  const [heureFin, setHeureFin] = useState(getLocalHourFormatted());
  const [checkdateFin, setCheckdateFin] = useState(false);

  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(
    defaultClient?.id
      ? { id: defaultClient.id, name: defaultClient.name ?? defaultClient.nom ?? "" }
      : null
  );
  const [customFieldValues, setCustomFieldValues] = useState({});

  const [selectedStatut, setSelectedStatut] = useState({
    value: "open",
    label: "Ouvert",
  });

  const [titre, setTitre] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPriorite, setSelectedPriorite] = useState(null);
  const [description, setDescription] = useState("");

  // --- SEPARATED STATES FOR COLLABORATORS ---
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Intervention
  const [interventionDateTime, setInterventionDateTime] = useState("");
  const [interventionDuree, setInterventionDuree] = useState("00:30");
  const [interventionDescription, setInterventionDescription] = useState("");
  const [interventionIntervenants, setInterventionIntervenants] = useState([]);

  // Facturation
  const [selectedFacturationMode, setSelectedFacturationMode] = useState(null);

  const { data: prioritiesData } = useTicketPriorities();
  const { data: typesData } = useTicketTypes();
  const { data: statutsData } = useTicketStatuts();
  const { data: usersData } = useUsers();
  const { data: groupesData } = useGroupes();
  const { data: clientsData } = useClients(1, 10, clientSearch);
  const isClientSearchTooShort = clientSearch.trim().length > 0 && clientSearch.trim().length < 3;
  const postTicketMutation = usePostTicket();
  
  const needsIntervention = useMemo(() => ['in_progress', 'closed', 'approved'].includes(selectedStatut?.value), [selectedStatut]);
  const needsBilling = useMemo(() => ['closed', 'approved'].includes(selectedStatut?.value), [selectedStatut]);
  
  const clientId = selectedClient?.id ?? null;
  const { data: facturationModesData } = useFacturationModes({ enabled: needsBilling });
  const { data: contratData } = useContratsByClientId(clientId, { enabled: needsBilling && !!clientId });
  const { user, hasPermission } = useAuth();

  // Options for selecting users
  const optionsUsers = useMemo(() => (usersData?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.name || ""} ${u.surname || ""}`.trim(),
  })), [usersData]);

  // Options for selecting groups
  const optionsGroups = useMemo(() => (groupesData?.data ?? []).map((g) => ({
    value: g.id,
    label: g.name,
  })), [groupesData]);

  // Status options considering permissions
  const optionsStatuts = useMemo(() => {
    const STATUT_LABELS = { open: "Ouvert", in_progress: "En cours", closed: "Clôturé", approved: "Approuvé" };
    const STATUT_PERMISSIONS = { closed: "CLOSE_TICKET", approved: "APPROVE_TICKET" };
    
    return (statutsData?.data ?? [])
      .filter((s) => !STATUT_PERMISSIONS[s.value] || hasPermission(STATUT_PERMISSIONS[s.value]))
      .map((s) => ({
        value: s.value,
        label: STATUT_LABELS[s.value] ?? s.name,
      }));
  }, [statutsData, hasPermission]);

  // Initialization when modal opens
  useEffect(() => {
    if (!showNewTicket) return;

    if (!selectedType && typesData?.data?.length > 0) {
      const def = typesData.data.find((t) => t.is_default) ?? typesData.data[0];
      setSelectedType({ value: def.id, label: def.label });
    }

    if (!selectedPriorite && prioritiesData?.data?.length > 0) {
      const def = prioritiesData.data[0];
      setSelectedPriorite({ value: def.id, label: def.label });
    }

    // Add current user to default list
    if (selectedUsers.length === 0 && user?.data?.id && optionsUsers.length > 0) {
      const me = optionsUsers.find((u) => u.value === user.data.id);
      if (me) setSelectedUsers([me]);
    }

    if (interventionIntervenants.length === 0 && user?.data?.id && optionsUsers.length > 0) {
      const me = optionsUsers.find((u) => u.value === user.data.id);
      if (me) setInterventionIntervenants([me]);
    }

    if (!interventionDateTime) {
      setInterventionDateTime(getLocalDateTimeFormatted());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewTicket, typesData, prioritiesData, optionsUsers, user]);

  const { data: clientChampsData } = useClientChamps(clientId);
  const clientChamps = clientChampsData?.data ?? [];
  const targetFieldStatus = TICKET_TO_FIELD_STATUS[selectedStatut?.value] ?? 'open';
  const applicableChamps = getApplicableChamps(clientChamps, null, targetFieldStatus);

  // Billing mode
  const hasContract = (contratData?.data?.length ?? 0) > 0;
  const optionsFacturation = useMemo(() => {
    const modes = [
      ...(hasContract ? [CONTRAT_MAINTENANCE] : []),
      ...(facturationModesData?.data ?? []).filter(m => m.id !== 1 && (m.is_active || m.is_default)),
    ];
    return modes.map(m => ({ value: m.id, label: m.label }));
  }, [hasContract, facturationModesData]);

  const resetForm = () => {
    setClientSearch("");
    setSelectedClient(null);
    setCustomFieldValues({});
    setSelectedStatut({ value: "open", label: "Ouvert" });
    setTitre("");
    setSelectedUsers([]);
    setSelectedGroups([]);
    setSelectedPriorite(null);
    setDescription("");
    setCheckdateFin(false);
    setDateDebut(getLocalDateFormatted());
    setHeureDebut(getLocalHourFormatted());
    setDateFin(getLocalDateFormatted());
    setHeureFin(getLocalHourFormatted());
    setInterventionDateTime("");
    setInterventionDescription("");
    setInterventionIntervenants([]);
    setSelectedFacturationMode(null);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!selectedClient) return toast.error("Veuillez sélectionner un client");
    if (!titre.trim()) return toast.error("Veuillez saisir un titre");
    if (!selectedType) return toast.error("Veuillez sélectionner un type");
    if (!selectedPriorite) return toast.error("Veuillez sélectionner une priorité");

    if (needsIntervention) {
      if (interventionIntervenants.length === 0) return toast.error("Veuillez sélectionner au moins un intervenant");
      if (!interventionDescription.trim()) return toast.error("Veuillez saisir une description d'intervention");
    }

    const ticketPayload = {
      titre,
      statut: selectedStatut.value,
      client: selectedClient.id,
      priorite: selectedPriorite.value,
      type: selectedType.value,
      description: description || null,
      date_debut: `${dateDebut}T${heureDebut}:00`,
      ...(checkdateFin && { date_fin: `${dateFin}T${heureFin}:00` }),
      // SEPARATED ARRAYS FOR API
      utilisateurs: selectedUsers.map(u => u.value),
      groupes: selectedGroups.map(g => g.value),
      ...(needsIntervention && {
        interventions: interventionIntervenants.map(i => ({
          description: interventionDescription,
          date_debut: `${interventionDateTime}:00`,
          duree: ((time) => {
            const [h, m] = time.split(":").map(Number);
            return h * 60 + m;
          })(interventionDuree),
          utilisateur: i.value,
        }))
      }),
      ...(needsBilling && { methode_facturation: selectedFacturationMode?.value }),
    };

    setIsSubmitting(true);
    try {
      const response = await postTicketMutation.mutateAsync({ ticket: ticketPayload });
      const ticketId = response?.data?.id;

      const champEntries = Object.entries(customFieldValues).filter(([, v]) => v !== "" && v != null);
      if (ticketId && champEntries.length > 0) {
        await Promise.allSettled(champEntries.map(([id, val]) => postChampValeur(Number(id), ticketId, val)));
      }

      toast.success("Ticket créé avec succès !");
      resetForm();
      setShowNewTicket(false);
    } catch (error) {
      toast.error(`Erreur : ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={showNewTicket} onHide={() => { resetForm(); setShowNewTicket(false); }} size="lg">
      <Form onSubmit={handleSubmitForm}>
        <Modal.Header closeButton>
          <Modal.Title>Création nouveau ticket</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3">
            <Form.Group as={Col} md={8}>
              <Form.Label>Client *</Form.Label>
              <Select
                options={isClientSearchTooShort ? [] : (clientsData?.data?.clients ?? []).map(c => ({ value: c.id, label: c.name }))}
                value={selectedClient ? { value: selectedClient.id, label: selectedClient.name } : null}
                onChange={(opt) => setSelectedClient(opt ? { id: opt.value, name: opt.label } : null)}
                onInputChange={setClientSearch}
                inputValue={clientSearch}
                filterOption={() => true}
                placeholder="Chercher un client..."
                isClearable
                styles={customSelectStyles}
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Statut *</Form.Label>
              <Select
                options={optionsStatuts}
                value={selectedStatut}
                onChange={setSelectedStatut}
                styles={customSelectStyles}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Titre *</Form.Label>
              <Form.Control value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Résumé du problème..." />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Type *</Form.Label>
              <Select options={typesData?.data?.map(t => ({ value: t.id, label: t.label }))} value={selectedType} onChange={setSelectedType} styles={customSelectStyles} />
            </Form.Group>
            <Form.Group as={Col} md={3}>
              <Form.Label>Priorité *</Form.Label>
              <Select options={prioritiesData?.data?.map(p => ({ value: p.id, label: p.label }))} value={selectedPriorite} onChange={setSelectedPriorite} styles={customSelectStyles} />
            </Form.Group>
            <Form.Group as={Col} md={5}>
              <Form.Label>Date de début</Form.Label>
              <Form.Control type="datetime-local" value={`${dateDebut}T${heureDebut}`} onChange={(e) => {
                const [d, t] = e.target.value.split("T");
                setDateDebut(d || ""); setHeureDebut(t || "");
              }} />
            </Form.Group>
          </Row>

          <div className="mb-3">
            <Form.Check
              type="checkbox"
              label="Définir une date de fin"
              checked={checkdateFin}
              onChange={(e) => setCheckdateFin(e.target.checked)}
              className="text-muted small"
            />
            {checkdateFin && (
              <Row className="mt-2">
                <Form.Group as={Col} md={5}>
                  <Form.Control type="datetime-local" value={`${dateFin}T${heureFin}`} onChange={(e) => {
                    const [d, t] = e.target.value.split("T");
                    setDateFin(d || ""); setHeureFin(t || "");
                  }} />
                </Form.Group>
              </Row>
            )}
          </div>

          {/* --- USER AND GROUP SELECTION FIELDS --- */}
          <Row className="mb-3">
            <Form.Group as={Col} md={6}>
              <Form.Label>Utilisateurs</Form.Label>
              <Select
                isMulti
                options={optionsUsers}
                value={selectedUsers}
                onChange={setSelectedUsers}
                styles={customSelectStyles}
                placeholder="Assigner des utilisateurs..."
              />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Groupes</Form.Label>
              <Select
                isMulti
                options={optionsGroups}
                value={selectedGroups}
                onChange={setSelectedGroups}
                styles={customSelectStyles}
                placeholder="Assigner des groupes..."
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Description du problème</Form.Label>
              <MarkdownEditor value={description} onChange={setDescription} rows={3} />
            </Form.Group>
          </Row>

          {needsIntervention && (
            <div
              className="border p-3 rounded mb-3"
              style={{
                backgroundColor: "var(--bs-secondary-bg)",
                borderColor: "var(--bs-border-color)",
              }}
            >
              <p className="fw-bold mb-2">Détails de l'intervention</p>
              <Row className="mb-3">
                <Form.Group as={Col} md={5}>
                  <Form.Label>Date/Heure *</Form.Label>
                  <Form.Control type="datetime-local" value={interventionDateTime} onChange={(e) => setInterventionDateTime(e.target.value)} />
                </Form.Group>
                <Form.Group as={Col} md={3}>
                  <Form.Label>Durée *</Form.Label>
                  <Form.Control type="time" value={interventionDuree} onChange={(e) => setInterventionDuree(e.target.value)} />
                </Form.Group>
                {needsBilling && (
                  <Form.Group as={Col} md={4}>
                    <Form.Label>Facturation *</Form.Label>
                    <Select options={optionsFacturation} value={selectedFacturationMode} onChange={setSelectedFacturationMode} styles={customSelectStyles} />
                  </Form.Group>
                )}
              </Row>
              <Row className="mb-3">
                <Form.Group as={Col}>
                  <Form.Label>Intervenant(s) *</Form.Label>
                  <Select isMulti options={optionsUsers} value={interventionIntervenants} onChange={setInterventionIntervenants} styles={customSelectStyles} />
                </Form.Group>
              </Row>
              <Form.Group>
                <Form.Label>Compte-rendu *</Form.Label>
                <MarkdownEditor value={interventionDescription} onChange={setInterventionDescription} rows={2} />
              </Form.Group>
            </div>
          )}

          {applicableChamps.length > 0 && (
            <div className="mt-4">
              <p className="fw-bold mb-2">Champs complémentaires</p>
              <CustomFieldsLayout
                champs={applicableChamps}
                values={customFieldValues}
                onChange={(id, val) => setCustomFieldValues(prev => ({ ...prev, [id]: val }))}
              />
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" disabled={isSubmitting} onClick={() => { resetForm(); setShowNewTicket(false); }}>
            Annuler
          </Button>
          <Button variant="warning" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Chargement..." : "Créer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewTicket;